import { createMachine } from 'xstate';

import shared from 'common/bento/shared';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import { AnyMachineEvent } from 'features/Bento/types/Abstract';

import actions, { Action } from './actions';
import guards, { Guard } from './guards';
import locales from './locales';
import model, { Context } from './model';
import services, { Service } from './services';
import { State } from './state';

const machine = createMachine<Context, AnyMachineEvent>(
  {
    context: model.initialContext,

    id: 'onboarding',

    initial: State.INIT,

    on: {
      '*': {
        actions: shared.actions.unhandled(),
      },
    },

    states: {
      [State.INIT]: {
        always: [
          {
            cond: Guard.IsMandatee,
            target: State.INIT_MANDATEE,
          },
          {
            target: State.INIT_NON_MANDATEE,
          },
        ],
      },

      [State.INIT_MANDATEE]: {
        always: [
          {
            cond: Guard.IsFirstOnboarding,
            target: State.WELCOME_MANDATEE,
          },
          {
            cond: Guard.ShouldSelectApplication,
            target: State.APPLICATION_SELECTION_MANDATEE,
          },
          {
            cond: Guard.IsCapitalDeposit,
            target: State.CAPITAL_DEPOSIT_MANDATEE,
          },
          {
            target: State.PROFILE_HELPER_MANDATEE,
          },
        ],
      },

      [State.INIT_NON_MANDATEE]: {
        /**
         * @todo handle refused companies here.
         */
        always: [
          {
            cond: Guard.IsInvitedAccountHolderCapitalDeposit,
            target: State.INVITED_ACCOUNT_HOLDER_CAPITAL_DEPOSIT,
          },

          {
            cond: Guard.IsInvitee,
            target: State.BENEFICIARY,
          },

          {
            cond: Guard.IsStandard,
            target: State.STANDARD,
          },

          {
            cond: Guard.IsSasuCreation,
            target: State.SASU_CREATION,
          },

          {
            cond: Guard.IsCapitalDeposit,
            target: State.CAPITAL_DEPOSIT,
          },

          {
            cond: Guard.IsShineStart,
            target: State.SHINE_START,
          },

          {
            target: State.PROFILE_HELPER,
          },
        ],
      },

      [State.INVITED_ACCOUNT_HOLDER_CAPITAL_DEPOSIT]: {
        on: {
          PREV: undefined,
        },
      },

      [State.BENEFICIARY]: {
        on: {
          PREV: undefined,
        },
      },

      [State.STANDARD]: {
        on: {
          PREV: {
            actions: Action.ClearApplicationType,
            target: State.PROFILE_HELPER,
          },
          SELECT_CAPITAL_DEPOSIT: { target: State.CAPITAL_DEPOSIT },
        },
      },

      [State.CAPITAL_DEPOSIT]: {
        on: {
          PREV: {
            actions: Action.ClearApplicationType,
            target: State.PROFILE_HELPER,
          },
        },
      },

      [State.SHINE_START]: {
        on: {
          PREV: {
            actions: Action.ClearApplicationType,
            target: State.PROFILE_HELPER,
          },
        },
      },

      [State.SASU_CREATION]: {
        on: {
          PREV: {
            actions: Action.ClearApplicationType,
            target: State.PROFILE_HELPER,
          },
        },
      },

      [State.PROFILE_HELPER]: {
        entry: [Action.ClearApplicationType, shared.actions.trackView()],

        on: {
          PREV: undefined,
          SELECT_BENEFICIARY: {
            actions: shared.actions.track({
              name: 'Onboarding_Selected_Beneficiary_Invitee_Link',
            }),
            target: State.REDIRECT_BENEFICIARY_INVITEE,
          },
          SELECT_CAPITAL_DEPOSIT: {
            actions: [
              shared.actions.identify({
                onboarding_account_type: 'capital_deposit',
              }),
              shared.actions.track({
                name: 'Onboarding_Selected_Capital_Deposit',
              }),
            ],
            target: State.START_APPLICATION,
          },
          SELECT_COMPANY_CREATION: {
            actions: [
              shared.actions.identify({
                onboarding_account_type: 'company_creation',
              }),
              shared.actions.track({
                name: 'Onboarding_Selected_Company_Creation',
              }),
            ],
            target: State.COMPANY_CREATION_LEGAL_FORM,
          },
          SELECT_SHINE_START: {
            actions: [
              shared.actions.identify({
                onboarding_account_type: 'shine_start_micro',
              }),
              shared.actions.track({
                name: 'Onboarding_Selected_Micro_Creation',
              }),
            ],
            target: State.START_APPLICATION,
          },
          SELECT_STANDARD: {
            actions: [
              shared.actions.identify({
                onboarding_account_type: 'regular_account_opening',
              }),
              shared.actions.track({
                name: 'Onboarding_Selected_Bank_Account_Opening',
              }),
            ],
            target: State.START_APPLICATION,
          },
        },
      },

      [State.START_APPLICATION]: {
        invoke: {
          onDone: [
            {
              cond: Guard.IsCapitalDeposit,
              target: State.CAPITAL_DEPOSIT,
            },
            {
              cond: Guard.IsShineStart,
              target: State.SHINE_START,
            },
            {
              cond: Guard.IsStandard,
              target: State.STANDARD,
            },
            {
              cond: Guard.IsSasuCreation,
              target: State.SASU_CREATION,
            },
            {
              target: State.PROFILE_HELPER,
            },
          ],
          onError: {
            actions: shared.actions.error(),
            target: State.PROFILE_HELPER,
          },
          src: Service.StartApplication,
        },
      },

      [State.REDIRECT_BENEFICIARY_INVITEE]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.PROFILE_HELPER,
        },
      },

      [State.PROFILE_HELPER_MANDATEE]: {
        entry: [shared.actions.trackView()],

        on: {
          PREV: [
            {
              cond: Guard.IsFirstOnboarding,
              target: State.WELCOME_MANDATEE,
            },
            { target: undefined },
          ],
          SELECT_CAPITAL_DEPOSIT_MANDATEE: State.CAPITAL_DEPOSIT_MANDATEE,
        },
      },

      [State.WELCOME_MANDATEE]: {
        entry: [shared.actions.trackView()],

        on: {
          NEXT: State.PROFILE_HELPER_MANDATEE,
          PREV: undefined,
        },
      },

      [State.APPLICATION_SELECTION_MANDATEE]: {
        entry: [shared.actions.trackView()],

        on: {
          PREV: undefined,
          // The ApplicationSelectionMandatee will call setCompanyContext
          // If the application hasn't been accessed yet, the whole onboarding will be unmounted:
          // some hooks in <SecuredRoutes/> will call queries with the new companyProfileId
          // If the application has been accessed yet, those queries will be cached, meaning this transition will occur
          SELECT_PENDING_MANDATEE_APPLICATION: State.INIT_MANDATEE,
          SELECT_START_NEW_MANDATEE_APPLICATION:
            State.SEND_NEW_MANDATEE_APPLICATION,
        },
      },

      [State.SEND_NEW_MANDATEE_APPLICATION]: {
        invoke: {
          onDone: {
            // When the company entity is created, the whole onboarding will be unmounted:
            // calling setCompanyContext will make some hooks in <SecuredRoutes/> call queries with the new companyProfileId
            // thus rendering a loader instead
            target: State.PROFILE_HELPER_MANDATEE,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.APPLICATION_SELECTION_MANDATEE,
          },
          src: Service.CreateNewAccount,
        },
      },

      [State.CAPITAL_DEPOSIT_MANDATEE]: {
        on: {
          NEXT: {
            actions: Action.ClearCompanyContext,
            target: State.APPLICATION_SELECTION_MANDATEE,
          },
          PREV: State.PROFILE_HELPER_MANDATEE,
        },
      },

      [State.COMPANY_CREATION_LEGAL_FORM]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.PROFILE_HELPER,
          SUBMIT_COMPANY_CREATION_LEGAL_FORM: [
            {
              actions: Action.AssignCompanyLegalForm,
              cond: Guard.ShouldDoInHouseSasuCreation,
              target: State.SASU_CREATION_ELIGIBILITY_CHECK,
            },
            {
              actions: Action.AssignCompanyLegalForm,
              target: State.COMPANY_CREATION_LANDBOT,
            },
          ],
        },
      },

      [State.COMPANY_CREATION_LANDBOT]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.COMPANY_CREATION_LEGAL_FORM,
          SELECT_CAPITAL_DEPOSIT: State.CAPITAL_DEPOSIT,
          // It shouldn't happen, as Landbot redirects Shine Start users to https://www.shine.fr/declaration-auto-entrepreneur/
          SELECT_SHINE_START: {
            actions: shared.actions.toast({
              status: 'warning',
              title: locales.unexpectedShineStartChoice,
            }),
          },
        },
      },

      [State.SASU_CREATION_ELIGIBILITY_CHECK]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.COMPANY_CREATION_LEGAL_FORM,
          SELECT_SASU_CREATION: State.START_APPLICATION,
        },
      },

      [State.DONE]: {
        data: {
          status: BentoModuleDoneStatus.DONE,
        },
        type: 'final',
      },
    },
  },

  {
    actions,
    guards,
    services,
  },
);

export default machine;
